/**
 * Snap Indicator Component
 *
 * Visual feedback for active snap points: a single marker glyph at the snap
 * position (2D behavior — the marker is the only feedback, no text label).
 * The glyph is pixel-fixed like a classic OSNAP marker: constant on-screen
 * size at every zoom level.
 */

import React, { useMemo, useState } from 'react';
import { Line } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useSnapStore, SnapType, pixelsToWorld } from './SnapManager';
import { SNAP_COLORS } from '../../themes/colors';

interface SnapIndicatorProps {
    /** Marker half-extent in SCREEN pixels (AutoCAD-style fixed glyph). */
    size?: number;
}

/** Half-extent of the OSNAP glyph in screen pixels. */
const SNAP_MARKER_HALF_PX = 6;
/** Re-render threshold for zoom-driven size changes (relative fraction). */
const SIZE_EPSILON = 0.08;

/**
 * Snap Indicator - Renders visual marker at active snap point
 */
export const SnapIndicator: React.FC<SnapIndicatorProps> = ({ size = SNAP_MARKER_HALF_PX }) => {
    const activeSnap = useSnapStore((s) => s.activeSnap);
    const { camera, gl } = useThree();
    const [worldSize, setWorldSize] = useState(0.15);

    // camera.zoom mutates in place (no store write), so the pixel-true
    // marker size is refreshed per frame behind a content guard — React
    // re-renders only when the glyph would actually change size.
    useFrame(() => {
        if (!activeSnap) return;
        const next = pixelsToWorld(size, activeSnap.position, camera, gl);
        setWorldSize((prev) => (
            Math.abs(prev - next) / Math.max(next, 1e-6) > SIZE_EPSILON ? next : prev
        ));
    });

    if (!activeSnap) return null;

    const { type, position } = activeSnap;

    return (
        <group position={[position.x, position.y + 0.01, position.z]}>
            <SnapMarker type={type} size={worldSize} />
        </group>
    );
};

/**
 * Snap Marker - Different shapes for different snap types
 */
const SnapMarker: React.FC<{ type: SnapType; size: number }> = ({ type, size }) => {
    const color = getSnapColor(type);

    const marker = useMemo(() => {
        switch (type) {
            case 'endpoint':
                return <EndpointMarker size={size} color={color} />;
            case 'midpoint':
                return <MidpointMarker size={size} color={color} />;
            case 'center':
                return <CenterMarker size={size} color={color} />;
            case 'intersection':
                return <IntersectionMarker size={size} color={color} />;
            case 'perpendicular':
                return <PerpendicularMarker size={size} color={color} />;
            case 'quadrant':
                return <QuadrantMarker size={size} color={color} />;
            case 'tangent':
                return <TangentMarker size={size} color={color} />;
            case 'nearest':
                return <NearestMarker size={size} color={color} />;
            case 'node':
                return <NodeMarker size={size} color={color} />;
            default:
                return <DefaultMarker size={size} color={color} />;
        }
    }, [type, size, color]);

    return marker;
};

// ============================================================================
// MARKER SHAPES
// ============================================================================

/**
 * Endpoint - Square
 */
const EndpointMarker: React.FC<{ size: number; color: string }> = ({ size, color }) => {
    const points = useMemo(() => [
        new THREE.Vector3(-size, 0, -size),
        new THREE.Vector3(size, 0, -size),
        new THREE.Vector3(size, 0, size),
        new THREE.Vector3(-size, 0, size),
        new THREE.Vector3(-size, 0, -size),
    ], [size]);

    return <Line points={points} color={color} lineWidth={2} />;
};

/**
 * Midpoint - Triangle
 */
const MidpointMarker: React.FC<{ size: number; color: string }> = ({ size, color }) => {
    const points = useMemo(() => [
        new THREE.Vector3(0, 0, -size),
        new THREE.Vector3(-size, 0, size),
        new THREE.Vector3(size, 0, size),
        new THREE.Vector3(0, 0, -size),
    ], [size]);

    return <Line points={points} color={color} lineWidth={2} />;
};

/**
 * Center - Circle
 */
const CenterMarker: React.FC<{ size: number; color: string }> = ({ size, color }) => {
    const points = useMemo(() => {
        const pts: THREE.Vector3[] = [];
        const segments = 16;
        for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            pts.push(new THREE.Vector3(
                Math.cos(angle) * size,
                0,
                Math.sin(angle) * size
            ));
        }
        return pts;
    }, [size]);

    return <Line points={points} color={color} lineWidth={2} />;
};

/**
 * Intersection - X cross
 */
const IntersectionMarker: React.FC<{ size: number; color: string }> = ({ size, color }) => {
    return (
        <group>
            <Line
                points={[
                    new THREE.Vector3(-size, 0, -size),
                    new THREE.Vector3(size, 0, size),
                ]}
                color={color}
                lineWidth={2}
            />
            <Line
                points={[
                    new THREE.Vector3(size, 0, -size),
                    new THREE.Vector3(-size, 0, size),
                ]}
                color={color}
                lineWidth={2}
            />
        </group>
    );
};

/**
 * Perpendicular - Right angle
 */
const PerpendicularMarker: React.FC<{ size: number; color: string }> = ({ size, color }) => {
    const points = useMemo(() => [
        new THREE.Vector3(-size, 0, 0),
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(0, 0, size),
    ], [size]);

    return <Line points={points} color={color} lineWidth={2} />;
};

/**
 * Quadrant - Diamond
 */
const QuadrantMarker: React.FC<{ size: number; color: string }> = ({ size, color }) => {
    const points = useMemo(() => [
        new THREE.Vector3(0, 0, -size),
        new THREE.Vector3(size, 0, 0),
        new THREE.Vector3(0, 0, size),
        new THREE.Vector3(-size, 0, 0),
        new THREE.Vector3(0, 0, -size),
    ], [size]);

    return <Line points={points} color={color} lineWidth={2} />;
};

/**
 * Tangent - Arc segment
 */
const TangentMarker: React.FC<{ size: number; color: string }> = ({ size, color }) => {
    const points = useMemo(() => {
        const pts: THREE.Vector3[] = [];
        const segments = 8;
        for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI - Math.PI / 2;
            pts.push(new THREE.Vector3(
                Math.cos(angle) * size,
                0,
                Math.sin(angle) * size
            ));
        }
        return pts;
    }, [size]);

    return <Line points={points} color={color} lineWidth={2} />;
};

/**
 * Nearest - Hourglass
 */
const NearestMarker: React.FC<{ size: number; color: string }> = ({ size, color }) => {
    const points = useMemo(() => [
        new THREE.Vector3(-size, 0, -size),
        new THREE.Vector3(size, 0, size),
        new THREE.Vector3(-size, 0, size),
        new THREE.Vector3(size, 0, -size),
        new THREE.Vector3(-size, 0, -size),
    ], [size]);

    return <Line points={points} color={color} lineWidth={2} />;
};

/**
 * Node - Plus sign
 */
const NodeMarker: React.FC<{ size: number; color: string }> = ({ size, color }) => {
    return (
        <group>
            <Line
                points={[
                    new THREE.Vector3(-size, 0, 0),
                    new THREE.Vector3(size, 0, 0),
                ]}
                color={color}
                lineWidth={2}
            />
            <Line
                points={[
                    new THREE.Vector3(0, 0, -size),
                    new THREE.Vector3(0, 0, size),
                ]}
                color={color}
                lineWidth={2}
            />
        </group>
    );
};

/**
 * Default - Small dot
 */
const DefaultMarker: React.FC<{ size: number; color: string }> = ({ size, color }) => {
    return (
        <mesh>
            <sphereGeometry args={[size * 0.3, 8, 8]} />
            <meshBasicMaterial color={color} />
        </mesh>
    );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getSnapColor(type: SnapType): string {
    // Handle types not present in SNAP_COLORS, like 'extension' and 'parallel'
    return SNAP_COLORS[type as keyof typeof SNAP_COLORS] || SNAP_COLORS.node;
}

export default SnapIndicator;
